import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Textarea,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Code,
  Alert,
  AlertIcon,
  Flex,
  FormControl,
  FormLabel,
  Input,
  IconButton,
  Select,
  Switch,
} from "@chakra-ui/react";
import { modelOptions } from "utils/helperFuncUtils";
import { FiTrash2 } from "react-icons/fi";
import showToast from "hooks/useCustomToast";
import PromptGenerationModal from "./PromptGenerationModal";
import ConfirmationModal from "../common/ConfirmationModal";
import { AgentConfig, PromptConfig, ReasoningEffortLevel, reasoningEffortLevelFromJSON, reasoningEffortLevelToJSON } from "build-ts/agent_config";
import {
  getAssignedPrompts,
  getPromptByid,
  updatePrompt,
  deletePrompt,
  duplicatePrompt
} from "services/EvaServices";
import { useUser } from "context/UserContext";
import { epochConverterWithTimeZone } from "utils/dateUtils";

interface EvaRightPanelProps {
  agentConfig: AgentConfig;
  promptConfig?: PromptConfig;
  promptEditor: string;
  onPromptEditorChange: (value: string) => void;
  onPromptConfigChange?: (field: string, value: any) => void;
  analysis_flag?: string;
  onConfigChange: (field: string, value: any) => void;
  isCreateMode?: boolean;
}

const EvaRightPanel: React.FC<EvaRightPanelProps> = ({
  agentConfig,
  promptConfig,
  promptEditor,
  onPromptEditorChange,
  onPromptConfigChange,
  analysis_flag = "",
  onConfigChange,
  isCreateMode = false,
}) => {
  const { defaultTimeZone } = useUser();
  const [testResult, setTestResult] = useState<string | null>(null);
  const [variableNames, setVariableNames] = useState<string[]>([]);
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});
  const [temperatureError, setTemperatureError] = useState<string>("");
  const [reasoningEffortLevel, setReasoningEffortLevel] = useState<ReasoningEffortLevel>(ReasoningEffortLevel.REASONING_NOT_VALID);

  // Prompt management states
  const [isPromptEditing, setIsPromptEditing] = useState(false);
  const [assignedPrompts, setAssignedPrompts] = useState<PromptConfig[]>([]);
  const [selectedPromptId, setSelectedPromptId] = useState<string>(promptConfig?.id || "");
  const [currentPrompt, setCurrentPrompt] = useState<PromptConfig | undefined>(promptConfig || agentConfig?.prompt_config);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDuplicateConfirmation, setShowDuplicateConfirmation] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);

  const { isOpen: isTestOpen, onOpen: onTestOpen, onClose: onTestClose } = useDisclosure();
  const { isOpen: isVarsOpen, onOpen: onVarsOpen, onClose: onVarsClose } = useDisclosure();
  const { isOpen: isGenerateOpen, onOpen: onGenerateOpen, onClose: onGenerateClose } = useDisclosure();

  // Check if temperature is valid for API calls
  const isTemperatureValid = () => {
    const tempValue = currentPrompt?.temperature;
    if (tempValue === undefined || tempValue === null) return false;

    return tempValue >= 0 && tempValue <= 2;
  };

  // Load assigned prompts for the agent (only for dropdown, not for initial data)
  const loadAssignedPrompts = React.useCallback(async () => {
    try {
      setIsLoadingPrompts(true);
      const prompts = await getAssignedPrompts(agentConfig.id || "");
      setAssignedPrompts(prompts || []);
    } catch (error) {
      console.error("Error loading assigned prompts:", error);
      showToast("Failed to load prompts", "", "error");
    } finally {
      setIsLoadingPrompts(false);
    }
  }, [agentConfig.id]);

  // Initialize prompt data from prompt config when it changes
  useEffect(() => {
    // TODO: Check why this useEffect is called infinitely - most likely due to the parents' callbacks used
    // as dependencies here
    if (promptConfig && !isCreateMode) {
      // Initialize form state from API data
      const initializedPrompt = {
        id: promptConfig.id,
        name: promptConfig.name || "",
        philosophy: promptConfig.philosophy || "",
        model_name: promptConfig.model_name || "gpt-4o",
        model_type: promptConfig.model_type || "openai",
        temperature: promptConfig.temperature || 0,
        is_active: promptConfig.is_active || false,
        instructions: promptConfig.instructions || "",
        created_date: promptConfig.created_date || 0,
        modified_date: promptConfig.modified_date || 0,
      };
      setCurrentPrompt(initializedPrompt);
      // Initialise reasoning effort level
      setReasoningEffortLevel(reasoningEffortLevelFromJSON(promptConfig.reasoning_effort || ReasoningEffortLevel.REASONING_NOT_VALID));

      // Update prompt editor with current prompt philosophy
      onPromptEditorChange(promptConfig.philosophy || "");

      // Update model configuration from current prompt
      if (onPromptConfigChange) {
        onPromptConfigChange("id", promptConfig.id || "");
        onPromptConfigChange("name", promptConfig.name || "");
        onPromptConfigChange("philosophy", promptConfig.philosophy || "");
        onPromptConfigChange("model_name", promptConfig.model_name || "gpt-4o");
        onPromptConfigChange("model_type", promptConfig.model_type || "openai");
        onPromptConfigChange("temperature", promptConfig.temperature || 0);
        onPromptConfigChange("is_active", promptConfig.is_active || false);
        onPromptConfigChange("instructions", promptConfig.instructions || "");
        onPromptConfigChange("created_date", promptConfig.created_date || "");
        onPromptConfigChange("modified_date", promptConfig.modified_date || "");
        onPromptConfigChange("reasoning_effort", promptConfig.reasoning_effort || ReasoningEffortLevel.REASONING_NOT_VALID);
      }
    }
  }, [promptConfig, isCreateMode, onPromptEditorChange, onPromptConfigChange]);


  // Load assigned prompts when component mounts
  useEffect(() => {
    if (!isCreateMode) {
      loadAssignedPrompts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateMode]);

  // Handle prompt selection change
  const handlePromptSelection = async (promptId: string) => {
    if (!promptId || promptId === selectedPromptId) return;

    try {
      const promptData = await getPromptByid(promptId);
      if (promptData) {
        // Update form state from selected prompt
        const updatedPrompt = {
          id: promptData.id,
          name: promptData.name || "",
          philosophy: promptData.philosophy || "",
          model_name: promptData.model_name || "gpt-4o",
          model_type: promptData.model_type || "openai",
          temperature: promptData.temperature || 0,
          is_active: promptData.is_active || false,
          instructions: promptData.instructions || "",
          created_date: promptData.created_date || 0,
          modified_date: promptData.modified_date || 0,
        };

        // Update states
        setCurrentPrompt(updatedPrompt);
        setSelectedPromptId(promptId);

        onPromptEditorChange(promptData.philosophy || "");

        // Update model configuration from selected prompt
        if (onPromptConfigChange) {
          onPromptConfigChange("id", promptData.id || "");
          onPromptConfigChange("name", promptData.name || "");
          onPromptConfigChange("philosophy", promptData.philosophy || "");
          onPromptConfigChange("model_name", promptData.model_name || "gpt-4o");
          onPromptConfigChange("model_type", promptData.model_type || "openai");
          onPromptConfigChange("temperature", promptData.temperature || 0);
          onPromptConfigChange("is_active", promptData.is_active || false);
          onPromptConfigChange("instructions", promptData.instructions || "");
          onPromptConfigChange("created_date", promptData.created_date || "");
          onPromptConfigChange("modified_date", promptData.modified_date || "");
        }
      }
    } catch (error) {
      console.error("Error loading prompt:", error);
      showToast("Failed to load prompt", "", "error");
    }
  };

  // Handle prompt update
  const handleUpdatePrompt = async () => {
    if (!currentPrompt) return;

    try {
      setIsUpdating(true);
      if (!isTemperatureValid()) {
        setTemperatureError("Temperature must be between 0 and 2");
        return;
      }
      const currentTimestamp = Date.now();
      const updatedPrompt: PromptConfig = {
        ...currentPrompt,
        name: currentPrompt.name,
        philosophy: currentPrompt.philosophy?.trim(),
        model_name: currentPrompt.model_name,
        model_type: currentPrompt.model_type,
        reasoning_effort: reasoningEffortLevel,
        temperature: currentPrompt.temperature,
        is_active: currentPrompt.is_active,
        modified_date: currentTimestamp,
      };

      await updatePrompt(currentPrompt.id || "", updatedPrompt);

      // Update the current prompt state with the new modified_date
      setCurrentPrompt(prev => ({
        ...prev,
        modified_date: currentTimestamp
      }));

      // Update the prompt config for parent component
      if (onPromptConfigChange) {
        onPromptConfigChange("modified_date", currentTimestamp);
      }

      showToast("Prompt updated successfully", "", "success");
      setIsPromptEditing(false);
      await loadAssignedPrompts(); // Reload prompts
    } catch (error) {
      console.error("Error updating prompt:", error);
      showToast("Failed to update prompt", "", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle prompt deletion
  const handleDeletePrompt = async () => {
    if (!currentPrompt) return;

    try {
      setIsDeleting(true);
      await deletePrompt(currentPrompt.id || "");
      showToast("Prompt deleted successfully", "", "success");

      // Reload prompts first to get updated list
      await loadAssignedPrompts();

      // Find and select the active prompt after deletion
      const prompts = await getAssignedPrompts(agentConfig.id || "");
      const activePrompt = prompts?.find((p: PromptConfig) => p.is_active);

      if (activePrompt) {
        // Load the active prompt
        await handlePromptSelection(activePrompt.id || "");
      } else {
        // If no active prompt, clear selection
        setCurrentPrompt(undefined);
        setSelectedPromptId("");
        onPromptEditorChange("");
      }
    } catch (error: any) {
      console.error("Error deleting prompt:", error);
      showToast("Failed to delete prompt", error.message || "", "error");
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  // Handle prompt duplication
  const handleDuplicatePrompt = async () => {
    if (!currentPrompt?.id) return;

    try {
      setIsDuplicating(true);
      const duplicated = await duplicatePrompt(currentPrompt.id);

      if (!duplicated?.id) {
        throw new Error("Failed to duplicate prompt - no ID returned");
      }

      // Reload prompts and select the new one
      await loadAssignedPrompts();

      const newPromptId = duplicated.id;
      setSelectedPromptId(newPromptId);

      try {
        const promptData = await getPromptByid(newPromptId);
        if (promptData) {
          const updatedPrompt = {
            id: promptData.id,
            name: promptData.name || "",
            philosophy: promptData.philosophy || "",
            model_name: promptData.model_name || "gpt-4o",
            model_type: promptData.model_type || "openai",
            temperature: promptData.temperature || 0,
            is_active: promptData.is_active || false,
            instructions: promptData.instructions || "",
            created_date: promptData.created_date || 0,
            modified_date: promptData.modified_date || 0,
          };
          setCurrentPrompt(updatedPrompt);

          onPromptEditorChange(promptData.philosophy || "");

          if (onPromptConfigChange) {
            onPromptConfigChange("id", promptData.id || "");
            onPromptConfigChange("name", promptData.name || "");
            onPromptConfigChange("philosophy", promptData.philosophy || "");
            onPromptConfigChange("model_name", promptData.model_name || "gpt-4o");
            onPromptConfigChange("model_type", promptData.model_type || "openai");
            onPromptConfigChange("temperature", promptData.temperature || 0);
            onPromptConfigChange("is_active", promptData.is_active || false);
            onPromptConfigChange("instructions", promptData.instructions || "");
            onPromptConfigChange("created_date", promptData.created_date || "");
            onPromptConfigChange("modified_date", promptData.modified_date || "");
          }
        }
      } catch (e) {
        // If fetching the new prompt fails, we still updated the list; show a toast
        console.error("Error loading duplicated prompt:", e);
        showToast("Prompt duplicated but failed to load details", "", "warning");
      }

      showToast("Prompt duplicated successfully", "", "success");
    } catch (error) {
      console.error("Error duplicating prompt:", error);
      showToast("Failed to duplicate prompt", "", "error");
    } finally {
      setIsDuplicating(false);
      setShowDuplicateConfirmation(false);
    }
  };

  const extractVariablesFromPrompt = (template: string): string[] => {
    const regex = /\{\{\s*([\w.]+)\s*\}\}/g;
    const matches = Array.from(template.matchAll(regex)).map((m) => m[1]);
    return Array.from(new Set(matches));
  };

  const renderTemplateWithValues = (template: string, values: Record<string, any>): string => {
    let rendered = template;

    // Replace exact matches
    Object.entries(values).forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, "g");
      rendered = rendered.replace(regex, Array.isArray(value) ? value.join(", ") : String(value));
    });

    return rendered;
  };

  const handleOpenValidateModal = () => {
    const vars = extractVariablesFromPrompt(promptEditor);
    setVariableNames(vars);
    const initialValues: Record<string, string> = {};
    vars.forEach((v) => (initialValues[v] = variableValues[v] ?? ""));
    setVariableValues(initialValues);
    onVarsOpen();
  };


  const handleChangeVariableValue = (name: string, value: string) => {
    setVariableValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleRunValidateTest = () => {
    try {
      const renderedTemplate = renderTemplateWithValues(promptEditor, variableValues);
      setTestResult(renderedTemplate);
      onVarsClose();
      onTestOpen();
    } catch (e) {
      setTestResult("❌ Rendering failed. Please check your variables and template.");
      onVarsClose();
      onTestOpen();
    }
  };

  const handleGeneratePrompt = () => {
    onGenerateOpen();
  };

  const handlePromptGenerated = (generatedPrompt: string) => {
    onPromptEditorChange(generatedPrompt);
    onGenerateClose();
  };


  return (
    <>
      <Box className="eva-right-panel eva-small-scrollbar">
        <Box className="eva-prompt">
          <Box className="eva-prompt-content">
            {/* Prompt Management Header - Only show in edit mode, not create mode */}
            {!isCreateMode && (
              <VStack spacing={4} align="stretch" w="100%">
                <HStack spacing={4} align="center" justify="space-between" w="100%">
                  <FormControl maxW="250px">
                    <FormLabel fontSize="sm" mb={2} fontWeight="600">Select Prompt</FormLabel>
                    <Select
                      value={selectedPromptId}
                      onChange={(e) => handlePromptSelection(e.target.value)}
                      size="sm"
                      isDisabled={isLoadingPrompts || isUpdating}
                      placeholder={isLoadingPrompts ? "Loading prompts..." : "Select a prompt"}
                      bg="white"
                      borderColor="gray.300"
                    >
                      {assignedPrompts.map((prompt) => (
                        <option key={prompt.id} value={prompt.id}>
                          v{prompt.version}_{epochConverterWithTimeZone(prompt.modified_date || 0, "dd-MMM-yyyy", defaultTimeZone)} {prompt.is_active ? "🟢" : ""}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <HStack spacing={2}>
                    {!isPromptEditing ? (
                      <>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          variant="outline"
                          onClick={() => setIsPromptEditing(true)}
                          isDisabled={!currentPrompt}
                        >
                          Edit Prompt
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="purple"
                          variant="outline"
                          onClick={() => setShowDuplicateConfirmation(true)}
                          isDisabled={!currentPrompt}
                        >
                          Duplicate
                        </Button>
                        <IconButton
                          aria-label="Delete prompt"
                          icon={<FiTrash2 />}
                          size="sm"
                          colorScheme="red"
                          variant="outline"
                          onClick={() => setShowDeleteConfirmation(true)}
                          isDisabled={!currentPrompt}
                        />
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setIsPromptEditing(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="green"
                          onClick={() => handleUpdatePrompt()}
                          isLoading={isUpdating}
                        >
                          Save Prompt
                        </Button>
                      </>
                    )}
                  </HStack>
                </HStack>
              </VStack>
            )}

            {/* Model Parameters and Description - Side by Side with 3:1 ratio */}
            <HStack spacing={6} align="flex-start" w="100%">
              {/* Description Section - 3/4 width */}
              <Box flex="3">
                <Text fontSize="sm" color="gray.700" fontWeight="600" mb={2}>
                  Prompt Description
                </Text>
                <Textarea
                  name="prompt description"
                  value={currentPrompt?.name}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCurrentPrompt(prev => ({ ...prev, name: value }));
                    if (onPromptConfigChange) {
                      onPromptConfigChange("name", value);
                    }
                  }}
                  placeholder="Prompt description"
                  rows={modelOptions[(currentPrompt?.model_type || "") as keyof typeof modelOptions]?.some(m => m.model_name === currentPrompt?.model_name && (m.model_options as any)?.reasoning?.length > 0) ? 9 : 5}
                  isDisabled={!isPromptEditing && !isCreateMode}
                  borderColor="gray.200"
                  fontSize="sm"
                />
              </Box>

              {/* Model Parameters - 1/4 width */}
              <VStack spacing={4} align="stretch" flex="1">
                <FormControl>
                  <FormLabel fontSize="sm" mb={2} fontWeight="600">Model</FormLabel>
                  <Select
                    value={`${currentPrompt?.model_type}:${currentPrompt?.model_name}`}
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      const [provider, modelName] = selectedValue.split(":");

                      // get the first valid reasoning effort level for the selected model
                      const reasoningOptions = modelOptions[provider as keyof typeof modelOptions]?.find(m => m.model_name === modelName && (m.model_options as any)?.reasoning?.length > 0);
                      const defaultReasoningLevel = reasoningOptions
                        ? reasoningEffortLevelFromJSON((reasoningOptions.model_options as any)?.reasoning?.[0])
                        : ReasoningEffortLevel.REASONING_NOT_VALID;

                      // Update form state
                      setCurrentPrompt(prev => ({
                        ...prev,
                        model_name: modelName,
                        model_type: provider,
                        reasoning_effort: defaultReasoningLevel
                      }));

                      // Update prompt config for parent component
                      if (onPromptConfigChange) {
                        onPromptConfigChange("model_name", modelName);
                        onPromptConfigChange("model_type", provider);
                        onPromptConfigChange("reasoning_effort", defaultReasoningLevel);
                      }
                    }}
                    isDisabled={!isPromptEditing && !isCreateMode}
                    size="sm"
                    bg="white"
                    borderColor="gray.300"
                    _hover={{ borderColor: "blue.400" }}
                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                  >
                    {Object.entries(modelOptions).map(([provider, models]) => (
                      <optgroup key={provider} label={provider.replace("_", " ").toUpperCase()}>
                        {models.map(({model_name, model_options}) => (
                          <option key={`${provider}:${model_name}`} value={`${provider}:${model_name}`}>
                            {model_name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isInvalid={!!temperatureError}>
                  <FormLabel fontSize="sm" mb={2} fontWeight="600">Temperature</FormLabel>
                  <Input
                    size="sm"
                    type="number"
                    step="0.1"
                    value={currentPrompt?.temperature?.toString() || ""}
                    onChange={(e) => {
                      const inputValue = e.target.value;

                      if (inputValue === "") {
                        setTemperatureError("");
                        setCurrentPrompt(prev => ({ ...prev, temperature: 0 }));
                        if (onPromptConfigChange) {
                          onPromptConfigChange("temperature", 0);
                        }
                        return;
                      }

                      const numValue = parseFloat(inputValue);
                      if (isNaN(numValue)) {
                        setTemperatureError("Please enter a valid number");
                        return;
                      }

                      // Validate range before updating
                      if (numValue < 0 || numValue > 2) {
                        setTemperatureError("Temperature must be between 0 and 2");
                        return;
                      }

                      // Only update if valid
                      setTemperatureError("");
                      setCurrentPrompt(prev => ({ ...prev, temperature: numValue }));
                      if (onPromptConfigChange) {
                        onPromptConfigChange("temperature", numValue);
                      }
                    }}
                    onWheel={(e) => {
                      if (!isPromptEditing && !isCreateMode) return; // Don't allow wheel when disabled

                      e.preventDefault();
                      e.stopPropagation();

                      const currentValue = currentPrompt?.temperature ?? 0;
                      const delta = e.deltaY > 0 ? -0.1 : 0.1;
                      const newValue = Math.round((currentValue + delta) * 10) / 10;

                      // Clamp the value between 0 and 2
                      const clampedValue = Math.max(0, Math.min(2, newValue));

                      if (clampedValue !== currentValue) {
                        setTemperatureError("");
                        setCurrentPrompt(prev => ({ ...prev, temperature: clampedValue }));
                        if (onPromptConfigChange) {
                          onPromptConfigChange("temperature", clampedValue);
                        }
                      }
                    }}
                    placeholder="0"
                    bg="white"
                    borderColor="gray.300"
                    _hover={{ borderColor: "blue.400" }}
                    _focus={{
                      borderColor: "blue.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)"
                    }}
                    fontWeight="500"
                    isDisabled={!isPromptEditing && !isCreateMode}
                    color={!isPromptEditing && !isCreateMode ? "gray.400" : "black"}
                  />
                </FormControl>

                {/* Reasoning Effort - Only show for specific models */}
                {modelOptions[(currentPrompt?.model_type || "") as keyof typeof modelOptions]?.some(m => m.model_name === currentPrompt?.model_name && (m.model_options as any)?.reasoning?.length > 0) && (
                  <FormControl>
                    <FormLabel fontSize="sm" mb={2} fontWeight="600">Reasoning Effort</FormLabel>
                    <Select
                      // TODO: Check the type of the state reasoningEffortLevel here - it is probably inconsistent
                      // also research why do we need toJSON and fromJSON here - are the option values always strings?
                      value={reasoningEffortLevelToJSON(reasoningEffortLevel)}
                      onChange={(e) => {
                        const value = e.target.value;
                        setReasoningEffortLevel(reasoningEffortLevelFromJSON(value));
                        if (onPromptConfigChange) {
                          onPromptConfigChange("reasoning_effort", value);
                        }
                      }}
                      isDisabled={!isPromptEditing && !isCreateMode}
                      size="sm"
                      bg="white"
                      borderColor="gray.300"
                      _hover={{ borderColor: "blue.400" }}
                      _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                    >
                      {
                        ((modelOptions[currentPrompt?.model_type as keyof typeof modelOptions] || [])?.find((m: any) => m.model_name === currentPrompt?.model_name)?.model_options as any)?.reasoning?.map((level: ReasoningEffortLevel) => (
                          <option key={reasoningEffortLevelToJSON(level)} value={reasoningEffortLevelToJSON(level)}>
                            {reasoningEffortLevelToJSON(level).replace(/_/g, " ").toUpperCase()}
                          </option>
                        ))
                      }
                    </Select>
                  </FormControl>
                )}
              </VStack>
            </HStack>

            {temperatureError && (
              <Text fontSize="xs" color="red.500" mb={2}>
                {temperatureError}
              </Text>
            )}

            {/* System Prompt Section */}
            <VStack spacing={4} align="stretch" w="100%">
            {!isCreateMode ? (
              <HStack justify="space-between" align="center">
                <Text fontSize="sm" color="gray.700" fontWeight="600" mb={0}>
                  System Prompt
                </Text>
                  {(assignedPrompts.filter(
                    (p: PromptConfig) => p.is_active
                  )[0]?.id === currentPrompt?.id) ? (
                    <Box
                      fontSize="sm"
                      color="green"
                      borderColor="green.500"
                      borderWidth="1px"
                      borderRadius="md"
                      p={1}
                    >
                      Active
                    </Box>
                  ) : (
                    <FormControl width="fit-content">
                      <FormLabel m={1}>
                        Activate This Prompt
                        <Switch
                          isChecked={currentPrompt?.is_active}
                          onChange={(e) => {
                            const isActive = e.target.checked;
                            setCurrentPrompt(prev => ({ ...prev, is_active: isActive }));
                            if (onPromptConfigChange) {
                              onPromptConfigChange("is_active", isActive);
                            }
                          }}
                          colorScheme="blue"
                          ml={2}
                          mb={0}
                          isDisabled={!isPromptEditing && !isCreateMode}
                        />
                      </FormLabel>
                    </FormControl>
                  )}
                </HStack>
              ) : (
                <Text fontSize="sm" color="gray.700" fontWeight="600" mb={0} display="flex" alignItems="flex-start">
                  System Prompt
                </Text>
              )}
              <Textarea
                name="prompt-editor"
                value={currentPrompt?.philosophy}
                onChange={(e) => {
                  const value = e.target.value;
                  setCurrentPrompt(prev => ({ ...prev, philosophy: value }));
                  onPromptEditorChange(value);
                  if (onPromptConfigChange) {
                    onPromptConfigChange("philosophy", value);
                  }
                }}
                placeholder="Enter your system prompt here. Use Jinja template syntax for variables like {{agent_name}}, {{plan_name}}, {{MKG_INFO}}, etc."
                className="eva-textarea eva-small-scrollbar"
                isDisabled={!isPromptEditing && !isCreateMode}
                minH={isCreateMode ? "550px" : "498px"}
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: "blue.400" }}
                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                fontSize="sm"
                fontFamily="mono"
                resize="vertical"
              />

              {!isCreateMode ? (
                <HStack justify="space-between" align="center" w="100%">
                  <VStack>
                    <Text fontSize="x-small" color="gray.700" mb={0}>
                      Created at: {currentPrompt?.created_date ? epochConverterWithTimeZone(currentPrompt.created_date, "dd-MMM-yyyy hh:mm a", defaultTimeZone) : ""} {defaultTimeZone.toUpperCase()}
                    </Text>
                    <Text fontSize="x-small" color="gray.700" mb={0}>
                      Modified at: {currentPrompt?.modified_date ? epochConverterWithTimeZone(currentPrompt.modified_date || 0, "dd-MMM-yyyy hh:mm a", defaultTimeZone) : ""} {defaultTimeZone.toUpperCase()}
                    </Text>
                  </VStack>

                <HStack justify="flex-end" gap={3} pt={2}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenValidateModal}
                    borderColor="gray.300"
                    _hover={{ borderColor: "blue.400", bg: "blue.50" }}
                  >
                    Validate & Test
                  </Button>

                  <Button
                    colorScheme="blue"
                    size="sm"
                    onClick={handleGeneratePrompt}
                    isDisabled={String(agentConfig.agent_type) !== "TIGA"}
                  >
                    Generate
                  </Button>
                </HStack>
              </HStack>
            ): (
              <Flex justify="flex-end" gap={3} w="100%" pt={2}>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenValidateModal}
                    borderColor="gray.300"
                    _hover={{ borderColor: "blue.400", bg: "blue.50" }}
                  >
                    Validate & Test
                  </Button>

                  <Button
                    colorScheme="blue"
                    size="sm"
                    onClick={handleGeneratePrompt}
                    isDisabled={String(agentConfig.agent_type) !== "TIGA"}
                  >
                    Generate
                  </Button>
              </Flex>
            )}
            </VStack>
          </Box>
        </Box>
      </Box>

      {/* Delete Prompt Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeletePrompt}
        title="Delete Prompt"
        text={`Are you sure you want to delete the prompt "${currentPrompt?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColorScheme="red"
        isLoading={isDeleting}
      />

      {/* Duplicate Prompt Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDuplicateConfirmation}
        onClose={() => setShowDuplicateConfirmation(false)}
        onConfirm={handleDuplicatePrompt}
        title="Duplicate Prompt"
        text={`Are you sure you want to duplicate the prompt "${currentPrompt?.name}"?`}
        confirmText="Duplicate"
        cancelText="Cancel"
        confirmColorScheme="purple"
        isLoading={isDuplicating}
      />

      {/* Template Test Result Modal */}
      <Modal isOpen={isTestOpen} onClose={onTestClose} size="xl">
        <ModalOverlay />
        <ModalContent className="eva-modal-content">
          <ModalHeader>Generated System Prompt</ModalHeader>
          <ModalCloseButton />
          <ModalBody className="eva-modal-body eva-small-scrollbar">
            <Box>
              <Text color="gray.600" mb={4}>
                Generated system prompt for agent: {agentConfig.name} ({agentConfig.agent_type})
              </Text>

              <Box className="eva-test-result-box eva-small-scrollbar">
                <Code className="eva-test-result-code">
                  {testResult}
                </Code>
              </Box>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Variables Input Modal for Validate & Test */}
      <Modal isOpen={isVarsOpen} onClose={onVarsClose} size="xl">
        <ModalOverlay />
        <ModalContent className="eva-modal-content">
          <ModalHeader>Enter Template Variables</ModalHeader>
          <ModalCloseButton />
          <ModalBody className="eva-modal-body eva-small-scrollbar">
            <VStack spacing={4} align="stretch">
              {variableNames.length === 0 ? (
                <Alert status="info">
                  <AlertIcon />
                  <Text>
                    No variables detected in the prompt. Add <Code>{"{{variable}}"}</Code> placeholders and try again.
                  </Text>
                </Alert>
              ) : (
                variableNames.map((name) => (
                  <FormControl key={name}>
                    <FormLabel>{name}</FormLabel>
                    <Input
                      value={variableValues[name] ?? ""}
                      onChange={(e) => handleChangeVariableValue(name, e.target.value)}
                      placeholder={`Enter value for ${name}`}
                    />
                  </FormControl>
                ))
              )}
              <Flex justify="flex-end" gap={3} mt={2}>
                <Button variant="ghost" onClick={onVarsClose}>Cancel</Button>
                <Button colorScheme="blue" onClick={handleRunValidateTest} isDisabled={variableNames.length === 0}>
                  Run
                </Button>
              </Flex>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Prompt Generation Modal */}
      <PromptGenerationModal
        isOpen={isGenerateOpen}
        onClose={onGenerateClose}
        agentType={String(agentConfig.agent_type || "")}
        basePrompt={promptEditor}
        onPromptGenerated={handlePromptGenerated}
        analysis_flag={analysis_flag}
      />
    </>
  );
};

export default EvaRightPanel;
